import {
  FloatingArrow,
  FloatingPortal,
  Placement,
  arrow,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import classNames from "classnames";
import { ReactNode, useRef, useState } from "react";
import styles from "./Tooltip.module.scss";

const ARROW_HEIGHT = 6;
const ARROW_WIDTH = 12;
const GAP = 4;
const OPEN_DELAY_MS = 100;

export type TooltipTriggerProps = {
  ref: (node: HTMLElement | null) => void;
  isOpen: boolean;
} & Record<string, unknown>;

export type TooltipProps = {
  children: ReactNode;
  renderTrigger: (props: TooltipTriggerProps) => ReactNode;
  placement?: Placement;
  className?: string;
};

export function Tooltip({
  children,
  renderTrigger,
  placement = "top",
  className,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(ARROW_HEIGHT + GAP),
      flip({ fallbackAxisSideDirection: "start" }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { open: OPEN_DELAY_MS },
    handleClose: safePolygon(),
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  return (
    <>
      {renderTrigger({
        ref: refs.setReference,
        isOpen,
        ...getReferenceProps(),
      })}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={classNames(styles.tooltip, className)}
            {...getFloatingProps()}
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              className={styles.arrow}
              width={ARROW_WIDTH}
              height={ARROW_HEIGHT}
            />
            {children}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
